import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FaChartBar,FaMoon, FaBrain, FaVideo, FaExclamationTriangle, FaPencilAlt, FaRedo, FaFire, FaCoins } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// ✅ Styled Components
const StyledContainer = styled(Container)`
  background-color: #000;
  color: #fff;
  min-height: 100vh;
  padding: 20px;
`;

const ChapterCard = styled(Card)`
  background-color: #222;
  color: #fff;
  text-align: center;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 15px;
`;

const TopicBox = styled(Card)`
  background-color: #FF7C24;
  color: #fff;
  padding: 10px;
  border-radius: 10px;
  text-align: center;
  font-weight: bold;
  margin: 10px auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LessonButton = styled(Button)`
  background-color: ${({ unlocked }) => (unlocked ? '#232830' : '#444')};
  border-color: ${({ unlocked }) => (unlocked ? '#232830' : '#444')};
  color: ${({ unlocked }) => (unlocked ? '#fff' : '#777')};
  font-size: 22px;
  border-radius: 12px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 15px;
  box-shadow: ${({ unlocked }) => (unlocked ? '0px 4px 8px rgba(255, 255, 255, 0.2)' : 'none')};
  position: relative;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.1);
  }
`;

const ProOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
  cursor: pointer;
`;

const ProCard = styled(Card)`
  background-color: #E0F7FA;
  color: #232830;
  width: 90%;
  max-width: 500px;
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  position: relative;
  cursor: auto;
`;

const ProList = styled.ul`
  text-align: left;
  padding: 0;
  list-style-type: disc;
  margin: 10px 0 20px 20px;

  li {
    margin-bottom: 5px;
  }
`;

const CourseLanding = () => {
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [showProSubscription, setShowProSubscription] = useState(false);
  const courseId = "1";
  const db = getFirestore();

  useEffect(() => {
    const fetchChapters = async () => {
      try {
        const chaptersCollection = query(collection(db, 'chapters_new'), where('courseid', '==', courseId));
        const chaptersSnapshot = await getDocs(chaptersCollection);
        let fetchedChapters = chaptersSnapshot.docs.map((doc) => doc.data());

        fetchedChapters.sort((a, b) => a.chapterid.localeCompare(b.chapterid));
        setChapters(fetchedChapters);
      } catch (error) {
        console.error("❌ Error fetching chapters:", error);
      }
    };

    fetchChapters();
  }, [db, courseId]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const topicsCollection = collection(db, 'topics_new');
        const topicsSnapshot = await getDocs(topicsCollection);
        let fetchedTopics = topicsSnapshot.docs.map((doc) => doc.data());

        fetchedTopics.sort((a, b) => a.topicid.localeCompare(b.topicid));
        setTopics(fetchedTopics);
      } catch (error) {
        console.error("❌ Error fetching topics:", error);
      }
    };

    fetchTopics();
  }, [db]);

  const handleShowProSubscription = () => {
    setShowProSubscription(true);
  };

  const handleCloseProSubscription = () => {
    setShowProSubscription(false);
    navigate("/course-landing");
  };

  const getLessonIcon = (lessonId) => {
    const lessonTypes = {
      "1": <FaMoon />,
      "2": <FaChartBar />,
      "3": <FaVideo />,
      "4": <FaExclamationTriangle />,
      "5": <FaPencilAlt />,
      "6": <FaBrain />,
      "7": <FaRedo />
    };
    return lessonTypes[lessonId.charAt(lessonId.length - 1)] || <FaMoon />;
  };

  return (
    <StyledContainer>
      {/* ✅ User Stats Bar */}
      <Row className="text-center mb-4">
      <Col>
      <FaFire style={{ color: '#2ECC71', fontSize: '24px' }} /> <span>1</span>
  </Col>
  <Col>
    <FaCoins style={{ color: '#FF7C24', fontSize: '24px' }} /> <span>10</span>
  </Col>
  <Col>
    <FaChartBar style={{ color: '#FF7C24', fontSize: '24px' }} /> <span>5</span>
  </Col>
  </Row>

      {/* ✅ Chapters List */}
      {chapters.map((chapter) => (
        <div key={chapter.chapterid} className="my-4">
          <ChapterCard>
            <h5>{chapter.chaptername}</h5>
          </ChapterCard>

          {topics.filter((topic) => topic.chapterid === chapter.chapterid).map((topic) => {
            const lessons = Array.from({ length: 13 }, (_, i) => `${topic.topicid}.${i + 1}`);

            return (
              <div key={topic.topicid} className="mb-4">
                <TopicBox>
                  <span>{topic.topicname}</span>
                </TopicBox>
                <div className="d-flex flex-column align-items-center">
                  {lessons.map((lessonId) => (
                    <div key={lessonId} className="d-flex justify-content-center mt-3">
                      <LessonButton unlocked onClick={handleShowProSubscription}>
                        {getLessonIcon(lessonId)}
                      </LessonButton>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* ✅ Pro Subscription Overlay */}
      {showProSubscription && (
        <ProOverlay onClick={handleCloseProSubscription}>
          <ProCard onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontWeight: "bold" }}>Upgrade to FinTorro Pro</h2>
            <p>Upgrade to FinTorro Pro and get:</p>
            <ProList>
              <li>Unlimited Lessons</li>
              <li>Unlimited Courses</li>
              <li>Unlimited Practice Sessions</li>
              <li>Unlimited Mistakes</li>
              <li>Learn with AI</li>
              <li>10,000 Lessons</li>
              <li>Join Challenges</li>
              <li>Includes Crypto, Forex, Psychology</li>
            </ProList>
            <Button
              style={{ backgroundColor: "#FF7C24", borderColor: "#FF7C24", width: "100%" }}
              onClick={() => window.open("https://buy.stripe.com/eVa29y1Ig8zsgFycMZ", "_blank")}
            >
              Join Now
            </Button>
          </ProCard>
        </ProOverlay>
      )}
    </StyledContainer>
  );
};

export default CourseLanding;






// import React, { useState, useEffect } from 'react';
// import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
// import { Container, Row, Col, Card, Button } from 'react-bootstrap';
// import { FaMoon, FaChartBar, FaVideo, FaBrain, FaExclamationTriangle, FaPencilAlt, FaRedo } from 'react-icons/fa';
// import { useNavigate } from 'react-router-dom';
// import styled from 'styled-components';

// // ✅ Styled Components
// const StyledContainer = styled(Container)`
//   background-color: #000;
//   color: #fff;
//   min-height: 100vh;
//   padding: 20px;
// `;

// const ChapterCard = styled(Card)`
//   background-color: #222;
//   color: #fff;
//   text-align: center;
//   border-radius: 10px;
//   padding: 15px;
//   margin-bottom: 15px;
// `;

// const TopicBox = styled(Card)`
//   background-color: #FF7C24;
//   color: #fff;
//   padding: 10px;
//   border-radius: 10px;
//   text-align: center;
//   font-weight: bold;
//   margin: 10px auto;
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
// `;

// const LessonButton = styled(Button)`
//   background-color: ${({ unlocked }) => (unlocked ? '#232830' : '#444')};
//   border-color: ${({ unlocked }) => (unlocked ? '#232830' : '#444')};
//   color: ${({ unlocked }) => (unlocked ? '#fff' : '#777')};
//   font-size: 22px;
//   border-radius: 12px;
//   width: 60px; /* ✅ Increased tap area */
//   height: 60px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   margin: 10px 15px; /* ✅ Updated spacing */
//   box-shadow: ${({ unlocked }) => (unlocked ? '0px 4px 8px rgba(255, 255, 255, 0.2)' : 'none')};
//   position: relative;
//   transition: transform 0.2s ease-in-out;

//   &:hover {
//     transform: scale(1.1);
//   }
// `;

// const LockedTopicsContainer = styled.div`
//   background-color: #222;
//   padding: 15px;
//   border-radius: 10px;
//   text-align: center;
//   margin-top: 20px;
// `;

// const CourseLanding = () => {
//   const navigate = useNavigate();
//   const [chapters, setChapters] = useState([]);
//   const [topics, setTopics] = useState([]);
//   const courseId = "1"; // ✅ Hardcoded for testing
//   const db = getFirestore();

//   // ✅ Fetch Chapters (Sorted in Ascending Order)
//   useEffect(() => {
//     const fetchChapters = async () => {
//       try {
//         const chaptersCollection = query(collection(db, 'chapters_new'), where('courseid', '==', courseId));
//         const chaptersSnapshot = await getDocs(chaptersCollection);
//         let fetchedChapters = chaptersSnapshot.docs.map((doc) => doc.data());

//         fetchedChapters.sort((a, b) => a.chapterid.localeCompare(b.chapterid));
//         setChapters(fetchedChapters);
//       } catch (error) {
//         console.error("❌ Error fetching chapters:", error);
//       }
//     };

//     fetchChapters();
//   }, [db, courseId]);

//   // ✅ Fetch Topics (Sorted in Ascending Order)
//   useEffect(() => {
//     const fetchTopics = async () => {
//       try {
//         const topicsCollection = collection(db, 'topics_new');
//         const topicsSnapshot = await getDocs(topicsCollection);
//         let fetchedTopics = topicsSnapshot.docs.map((doc) => doc.data());

//         fetchedTopics.sort((a, b) => a.topicid.localeCompare(b.topicid));
//         setTopics(fetchedTopics);
//       } catch (error) {
//         console.error("❌ Error fetching topics:", error);
//       }
//     };

//     fetchTopics();
//   }, [db]);

//   // ✅ Generate 13 Lessons for a Topic in T-Shape
//   const generateTShapeLessons = (baseLessonId) => {
//     return Array.from({ length: 13 }, (_, i) => `${baseLessonId}.${i + 1}`);
//   };

//   // ✅ Get Lesson Icon Based on Type
//   const getLessonIcon = (lessonId) => {
//     const lessonTypes = {
//       "1": <FaMoon />,
//       "2": <FaChartBar />,
//       "3": <FaVideo />,
//       "4": <FaExclamationTriangle />,
//       "5": <FaPencilAlt />,
//       "6": <FaBrain />,
//       "7": <FaRedo />
//     };
//     return lessonTypes[lessonId.charAt(lessonId.length - 1)] || <FaMoon />;
//   };

//   return (
//     <StyledContainer>
//       {/* ✅ User Stats Bar */}
//       <Row className="text-center mb-4">
//         <Col>
//           <FaChartBar style={{ color: '#FF7C24', fontSize: '24px' }} /> <span>1</span>
//         </Col>
//         <Col>
//           <FaBrain style={{ color: '#FF7C24', fontSize: '24px' }} /> <span>1</span>
//         </Col>
//         <Col>
//           <FaMoon style={{ color: '#2ECC71', fontSize: '24px' }} /> <span>5</span>
//         </Col>
//       </Row>

//       {/* <h2 className="text-center mb-4">Course ID {courseId}</h2> */}

//       {/* ✅ Chapters List */}
//       {chapters.map((chapter) => (
//         <div key={chapter.chapterid} className="my-4">
//           <ChapterCard>
//             <h5>{chapter.chaptername}</h5>
//           </ChapterCard>

//           {/* ✅ Topics (Now in Ascending Order) */}
//           {topics.filter((topic) => topic.chapterid === chapter.chapterid).map((topic) => {
//             const lessons = generateTShapeLessons(topic.topicid);

//             return (
//               <div key={topic.topicid} className="mb-4">
//                 <TopicBox>
//                   <span>{topic.topicname}</span>
//                 </TopicBox>

//                 {/* ✅ T-SHAPE LAYOUT */}
//                 <div className="d-flex flex-column align-items-center">
//                   {/* Row 1 - Three Icons (Evenly Spaced with Adjusted Horizontal Gaps) */}
//                   <div className="d-flex justify-content-center">
//                     <LessonButton unlocked style={{ marginRight: "12px" }} onClick={() => navigate('/quiz', { state: { lessonId: lessons[0] } })}>
//                       {getLessonIcon(lessons[0])}
//                     </LessonButton>
//                     <LessonButton unlocked onClick={() => navigate('/quiz', { state: { lessonId: lessons[1] } })}>
//                       {getLessonIcon(lessons[1])}
//                     </LessonButton>
//                     <LessonButton unlocked style={{ marginLeft: "12px" }} onClick={() => navigate('/quiz', { state: { lessonId: lessons[2] } })}>
//                       {getLessonIcon(lessons[2])}
//                     </LessonButton>
//                   </div>

//                   {/* Row 2-10 - Center Aligned Lessons with Vertical Spacing */}
//                   {lessons.slice(3).map((lessonId, index) => (
//                     <div key={lessonId} className="d-flex justify-content-center" style={{ marginTop: '12px' }}>
//                       <LessonButton unlocked onClick={() => navigate('/quiz', { state: { lessonId } })}>
//                         {getLessonIcon(lessonId)}
//                       </LessonButton>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       ))}

//       {/* ✅ Locked Topics */}
//       <LockedTopicsContainer>
//         <p>Complete all above lessons to unlock advanced topics.</p>
//       </LockedTopicsContainer>
//     </StyledContainer>
//   );
// };

// export default CourseLanding;
